import { hasInsurance } from './rate-grouping';

import type { LtlQuote } from '../../types/ltl';

// Reject auto-selection if the closest match diverges by more than 25% — guards
// against silently picking a materially wrong price when a carrier's rate changes
// substantially between quote calls.
const MAX_RELATIVE_PRICE_DEVIATION = 0.25;

/**
 * Reconciles a previously selected quote against a fresh set of rates. offerId
 * is unstable between quote calls, so we match on carrierScac + guaranteed flag,
 * then pick the candidate with the closest price. This handles minor price
 * fluctuations between re-quotes (e.g. fuel-surcharge adjustments) while still
 * preferring the correct service tier when a carrier offers multiple options at
 * different price points.
 *
 * When `isInsured` is provided, only candidates on the matching insurance side
 * (insured vs uninsured) are considered, preventing reconciliation from landing
 * on the uninsured twin of a carrier when insurance is enabled (or vice-versa).
 *
 * Returns null when there is no previous selection, no carrier/tier match, or
 * when the closest candidate deviates by more than MAX_RELATIVE_PRICE_DEVIATION.
 */
export const reconcileSelection = (previous: LtlQuote | null | undefined, newRates: LtlQuote[], isInsured?: boolean): LtlQuote | null => {
  if (!previous) {
    return null;
  }

  const candidates = newRates.filter(
    (rate) =>
      rate.carrierScac === previous.carrierScac &&
      rate.isGuaranteed === previous.isGuaranteed &&
      (isInsured === undefined || hasInsurance(rate) === isInsured),
  );

  if (candidates.length === 0) return null;

  const best = candidates.reduce((a, b) =>
    Math.abs(b.totalCharges - previous.totalCharges) < Math.abs(a.totalCharges - previous.totalCharges) ? b : a,
  );

  if (
    previous.totalCharges !== 0 &&
    Math.abs(best.totalCharges - previous.totalCharges) / previous.totalCharges > MAX_RELATIVE_PRICE_DEVIATION
  ) {
    return null;
  }

  return best;
};
